import { gsap } from 'gsap';

const selectors = {
  collectionSection: '.main-collection',
  filter: '[data-filter]',
  sort: '[data-sort]',
  sortLabel: '[data-sort-label]'
}

class CollectionFilters extends HTMLElement {
  #sectionId = null;
  #activeSort = { value: 'manual', label: 'Featured' };
  #activeFilter = 'all';

  constructor() {
    super();

    this.handleFilterClick = this.handleFilterClick.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.setupEvents = this.setupEvents.bind(this);
    this.destroyEvents = this.destroyEvents.bind(this);
  }

  updateFilters() {
    this.filters?.forEach(filter => {
      filter.setAttribute('data-active', filter.dataset.filter === this.#activeFilter ? 'true' : 'false');
    })
  }

  async updateCollection() {
    if (!this.collectionSectionId) return;

    this.setAttribute('data-loading', 'true');

    window.scrollTo({
      top: 120,
      behavior: 'smooth'
    })

    try {
      let endpoint = `?section_id=${this.#sectionId}`;
      if (this.#activeSort && this.#activeSort.value !== 'manual') {
        endpoint += `&sort_by=${this.#activeSort.value}`;
      }

      if (this.#activeFilter && this.#activeFilter !== 'all') {
        endpoint += `&${this.#activeFilter}`;
      }

      const response = await fetch(endpoint)
      const data = await response.text();
  
      const parser = new DOMParser();
      const parsedData = parser.parseFromString(data, 'text/html');

      gsap.to(this.collectionSection, {
        opacity: 0,
        duration: 0.45,
        ease: 'power2.out',
        onComplete: () => {
          this.collectionSection.innerHTML = parsedData.body.innerHTML;
          gsap.to(this.collectionSection, {
            opacity: 1,
            duration: 0.45,
            ease: 'power2.out'
          })
        }
      })
    } catch (error) {
      console.error(error);
    } finally {
      this.removeAttribute('data-loading');
    }
  }

  toggleFilter(filter) {
    if (!filter) return;

    if (filter === this.#activeFilter) return;

    this.#activeFilter = filter;

    this.updateCollection();
    this.updateFilters();
  }

  handleFilterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const filter = event.currentTarget.dataset.filter;

    this.toggleFilter(filter);
  }

  handleSortChange(event) {
    event.preventDefault();
    event.stopPropagation();

    const value = event.currentTarget.value;
    const label = this.sort.querySelector('option[value="' + value + '"]').textContent;

    this.#activeSort = {
      value,
      label
    };

    if (this.sortLabel) {
      this.sortLabel.textContent = label;
    }

    this.updateCollection();
  }

  setupEvents() {
    this.sort?.addEventListener('change', this.handleSortChange);
    this.filters?.forEach(filter => {
      filter.addEventListener('click', this.handleFilterClick);
    })
  }

  destroyEvents() {
    this.sort?.removeEventListener('change', this.handleSortChange);
    this.filters?.forEach(filter => {
      filter.removeEventListener('click', this.handleFilterClick);
    })
  }

  connectedCallback() {
    this.collectionSection = document.querySelector(selectors.collectionSection);
    this.collectionSectionId = this.collectionSection?.id;
    this.filters = this.querySelectorAll(selectors.filter);
    this.sort = this.querySelector(selectors.sort);
    this.sortLabel = this.querySelector(selectors.sortLabel);
    this.#sectionId = this.collectionSectionId.replace('shopify-section-', '');

    this.setupEvents();
  }

  disconnectedCallback() {
    this.destroyEvents();
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}